import { Autocomplete, Box, Grid, MenuItem, Chip, Typography, Stack, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Body1, H3, Subtitle1 } from '../../components/typography';
import { NumberInput, SelectInput } from '../../components/inputs';
import { COUNTRY_NAMES, CountryCode } from '../../models/country';
import { useTranslation } from 'react-i18next';
import { COUNTRY_TO_FLAG } from '../../utils/languageUtils';
import { Address, Vehicle, VehicleWithDriver } from '../../models/vehicle';
import { TruckDetails } from '../../types';
import { useEffect, useState } from 'react';
import TextInput from '../../components/inputs/textInput';
import { FormData } from '../../components/tables/searchTable/offerSearchTable';
import DateTimeInput from '../../components/inputs/DateTimeInput';
import { MarketplaceNameEnum } from '../../models/tenant';

type Props = {
  startFormData: FormData;
  endFormData: FormData;
  until?: string;
  marketplace?: string;
  enabledMarketplaces?: MarketplaceNameEnum[];
  selectedTruck?: TruckDetails | null;
  vehicles?: (Vehicle | VehicleWithDriver)[];
  errors?: string[];
  setStartFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setEndFormData: React.Dispatch<React.SetStateAction<FormData>>;
  setUntil: React.Dispatch<React.SetStateAction<string>>;
  setMarketplace: React.Dispatch<React.SetStateAction<string>>;
  onSearch: () => void;
};

export default function OfferSearchForm({
  startFormData,
  endFormData,
  until,
  marketplace,
  enabledMarketplaces = [],
  selectedTruck,
  vehicles = [],
  errors = [],
  setStartFormData,
  setEndFormData,
  setUntil,
  setMarketplace,
  onSearch
}: Props) {

  const { t } = useTranslation();

  const [usedLocation, setUsedLocation] = useState<string>('');

  // Find the vehicle object from selectedTruck
  const selectedVehicle = selectedTruck
    ? vehicles.find(v => v.id.toString() === selectedTruck.id)
    : null;

  // Auto-fill from truck location when modal opens (only if fields are empty)
  useEffect(() => {
    if (selectedVehicle && !startFormData.city && !startFormData.postal) {
      // Prefer emptyIn (delivery location) over currentPosition
      const locationToUse = selectedVehicle.emptyIn || selectedVehicle.currentPosition;

      if (locationToUse) {
        const countryName = COUNTRY_NAMES[locationToUse.country as CountryCode] || locationToUse.country;
        setStartFormData(prev => ({
          ...prev,
          mode: 'area',
          country: countryName,
          city: locationToUse.city || '',
          zip: locationToUse.postalCode || '',
          radius: '200'
        }));
      }
    }
  }, [selectedVehicle]);

  const fillFrom = (location: Address | undefined) => {
    if (!location) return;
    const countryName = COUNTRY_NAMES[location.country as CountryCode] || location.country;
    setStartFormData(prev => ({
      ...prev,
      mode: 'area',
      country: countryName,
      city: location.city || '',
      zip: location.postalCode || '',
      radius: '200'
    }));
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {selectedTruck && (
          <Grid size={12}>
            <H3>{t('Selected Truck: ')} {selectedTruck?.plate}</H3>
            <Subtitle1>{t('Current Location: ')} {selectedVehicle?.currentPosition?.city}, {COUNTRY_NAMES[selectedVehicle?.currentPosition?.country as CountryCode] || selectedVehicle?.currentPosition?.country}</Subtitle1>
            <Subtitle1>{t('Delivery In: ')} {selectedVehicle?.emptyIn ? `${selectedVehicle.emptyIn.city}, ${COUNTRY_NAMES[selectedVehicle.emptyIn.country as CountryCode] || selectedVehicle.emptyIn.country}` : t('N/A')}</Subtitle1>
          </Grid>
        )}
        {/* FROM SECTION */}
        <Grid size={6}>
          <Stack direction={'row'} justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.dark' }}>
              {t('FROM')}
            </Typography>
            {selectedVehicle && (
              <Stack direction={'row'} alignItems="center" spacing={1}>
                {selectedVehicle?.currentPosition && (
                  <Chip
                    label={t('Current')}
                    onClick={() => { fillFrom(selectedVehicle?.currentPosition); setUsedLocation('current'); }}
                    size='small'
                    sx={{
                      backgroundColor: usedLocation === 'current' ? '#5d5d5dff' : 'default',
                      color: usedLocation === 'current' ? 'text.white' : 'default'
                    }}
                  />
                )}
                {selectedVehicle?.emptyIn && (
                  <Chip
                    label={t('Delivery')}
                    onClick={() => { fillFrom(selectedVehicle?.emptyIn); setUsedLocation('delivery'); }}
                    size='small'
                    sx={{
                      backgroundColor: usedLocation === 'delivery' ? '#5d5d5dff' : 'default',
                      color: usedLocation === 'delivery' ? 'white' : 'default'
                    }}
                  />
                )}
              </Stack>
            )}
          </Stack>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextInput
              name="startMode"
              label={t('Location Mode')}
              fullWidth
              select
              value={startFormData.mode || 'area'}
              onChange={e => setStartFormData({ ...startFormData, mode: e.target.value as 'area' | 'postalCodeAreas' })}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'text.black'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'text.black'
                }
              }}
            >
              <MenuItem value="area">{t('Area')}</MenuItem>
              <MenuItem value="postalCodeAreas">{t('Postal Code Areas')}</MenuItem>
            </TextInput>

            {startFormData.mode === 'postalCodeAreas' ? (
              <Box>
                <PostalCodeForm
                  index={0}
                  setFormData={setStartFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={1}
                  setFormData={setStartFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={2}
                  setFormData={setStartFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={3}
                  setFormData={setStartFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={4}
                  setFormData={setStartFormData}
                  onSearch={onSearch}
                />
              </Box>
            ) : (
              <AreaForm
                formData={startFormData}
                setFormData={setStartFormData}
                onSearch={onSearch}
              />
            )}
          </Box>
        </Grid>

        {/* TO SECTION */}
        <Grid size={6}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.dark' }}>
              {t('TO')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextInput
              name="destMode"
              label={t('Location Mode')}
              fullWidth
              select
              value={endFormData.mode || 'area'}
              onChange={e => setEndFormData({ ...endFormData, mode: e.target.value as 'area' | 'postalCodeAreas' })}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'text.black'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'text.black'
                }
              }}
            >
              <MenuItem value="area">{t('Area')}</MenuItem>
              <MenuItem value="postalCodeAreas">{t('Postal Code Areas')}</MenuItem>
            </TextInput>

            {endFormData.mode === 'postalCodeAreas' ? (
              <Box>
                <PostalCodeForm
                  index={0}
                  setFormData={setEndFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={1}
                  setFormData={setEndFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={2}
                  setFormData={setEndFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={3}
                  setFormData={setEndFormData}
                  onSearch={onSearch}
                />
                <PostalCodeForm
                  index={4}
                  setFormData={setEndFormData}
                  onSearch={onSearch}
                />
              </Box>
            ) : (
              <AreaForm
                setFormData={setEndFormData}
                onSearch={onSearch}
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {/* UNTIL DATE */}
      <DateTimeInput
        type="date"
        name="until"
        label={t('Until')}
        fullWidth
        sx={{
          mt: 2,
          '& .MuiInputBase-root': {
            backgroundColor: 'background.white'
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.white'
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'background.black'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'background.black'
          }
        }}
        InputLabelProps={{ shrink: true }}
        value={until || ''}
        onChange={value => setUntil(value ? value.toISOString() : '')}
      />

      <SelectInput
        name="marketplace"
        label={t('Marketplace')}
        fullWidth
        sx={{ mt: 2 }}
        value={marketplace}
        onChange={e => setMarketplace(e.target.value)}
        options={[
          ...(enabledMarketplaces?.includes(MarketplaceNameEnum.TimocomMarketplace) ? [{ value: MarketplaceNameEnum.TimocomMarketplace, key: t('Timocom Marketplace') }] : []),
          ...(enabledMarketplaces?.includes(MarketplaceNameEnum.WtransnetMarketplace) ? [{ value: MarketplaceNameEnum.WtransnetMarketplace, key: t('Wtransnet Marketplace') }] : []),
          ...(enabledMarketplaces?.includes(MarketplaceNameEnum.LKWWalterMarketplace) ? [{ value: MarketplaceNameEnum.LKWWalterMarketplace, key: t('LKW Walter Marketplace') }] : [])
        ]}
      />

      {/* VALIDATION ERRORS */}
      {errors && errors.length > 0 && (
        <Box mt={2} p={2} bgcolor='#fdecea' border='1px solid #f5c6cb' borderRadius='4px'>
          {errors.map((error, index) => (
            <Body1 key={index} color='#a71d2a'>{error}</Body1>
          ))}
        </Box>
      )}

    </Box>
  );
}

const AreaForm = ({
  formData,
  setFormData,
  onSearch
}: {
  formData?: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSearch: () => void;
}) => {
  const { t } = useTranslation();

  const [form, setForm] = useState<{
    mode: 'area';
    country: string;
    city: string;
    zip: string;
    radius: string;
  }>({
    mode: 'area',
    country: formData?.country || '',
    city: formData?.city || '',
    zip: formData?.zip || '',
    radius: formData?.radius || ''
  });

  useEffect(() => {
    setForm({
      mode: 'area',
      country: formData?.country || '',
      city: formData?.city || '',
      zip: formData?.zip || '',
      radius: formData?.radius || ''
    });
  }, [formData]);

  const onChange = (field: string, value: string) => {
    setForm(prevData => ({ ...prevData, [field]: value }));
  };

  const onBlur = () => {
    setFormData(prev => ({ ...prev, ...form }));
  };

  return (
    <Stack spacing={1.5}>
      <Autocomplete
        options={Object.values(CountryCode).map((code) => ({
          label: COUNTRY_NAMES[code],
          value: COUNTRY_NAMES[code],
          code: code,
          flag: COUNTRY_TO_FLAG[code] || ''
        }))}
        getOptionLabel={(option) => option.label}
        value={form.country ? Object.values(CountryCode).map((code) => ({
          label: COUNTRY_NAMES[code],
          value: COUNTRY_NAMES[code],
          code: code,
          flag: COUNTRY_TO_FLAG[code] || ''
        })).find(opt => opt.value === form.country) || null : null}
        onChange={(e, value) => onChange('country', value ? value.value : '')}
        onBlur={() => onBlur()}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{option.flag}</span>
            <span>{option.label}</span>
          </Box>
        )}
        renderInput={(params) => {
          const selectedOption = form.country ? Object.values(CountryCode).map((code) => ({
            label: COUNTRY_NAMES[code],
            value: COUNTRY_NAMES[code],
            code: code,
            flag: COUNTRY_TO_FLAG[code] || ''
          })).find(opt => opt.value === form.country) : null;
          return (
            <TextInput
              {...params}
              name="country"
              label={t('Country')}
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedOption?.flag ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <span>{selectedOption.flag}</span>
                  </Box>
                ) : null,
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.white'
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'background.black'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: 'background.black'
                }
              }}
            />
          );
        }}
      />
      <TextInput
        name="city"
        label={t('City')}
        value={form.city || ''}
        onChange={e => onChange('city', e.target.value)}
        onBlur={() => onBlur()}
        onKeyDown={e => e.key === 'Enter' && onSearch()}
        placeholder="e.g. Madrid"
        fullWidth
      />
      <Grid container spacing={1}>
        <Grid size={6}>
          <TextInput
            name="zip"
            label={t('Postal Code')}
            value={form.zip || ''}
            onChange={e => onChange('zip', e.target.value)}
            onBlur={() => onBlur()}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
            placeholder="e.g. 28001"
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <NumberInput
            name="radius"
            label={t('Radius (km)')}
            value={form.radius || ''}
            onChange={e => onChange('radius', e.target.value)}
            onBlur={() => onBlur()}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
            placeholder="200"
            fullWidth
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

interface PostalCodeFormProps {
  index?: number;
  formData?: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSearch: () => void;
}

const PostalCodeForm = ({
  index,
  formData,
  setFormData,
  onSearch
}: PostalCodeFormProps) => {

  const { t } = useTranslation();
  const [form, setForm] = useState<{
    country: string;
    postalCodes: string[]
  }>({ country: '', postalCodes: [''] });

  useEffect(() => {
    const idx = index || 0;
    const existing = formData?.postal && formData.postal[idx] ? formData.postal[idx] : undefined;
    setForm({
      country: existing?.postalCountry || '',
      postalCodes: existing?.postalCodes && existing.postalCodes.length > 0 ? existing.postalCodes : ['']
    });
  }, [formData, index]);

  const handleChange = (field: string, value: string, index?: number) => {
    if (field === 'country') {
      setForm(prevData => ({ ...prevData, country: value }));
    } else if (field === 'postalCodes' && index !== undefined) {
      const updatedPostalCodes = [...form.postalCodes];
      updatedPostalCodes[index] = value;
      setForm(prevData => ({ ...prevData, postalCodes: updatedPostalCodes }));
    }
  };

  const onBlur = () => {
    setFormData(prev => {
      const updatedPostal = prev.postal ? [...prev.postal] : [{ postalCountry: '', postalCodes: [] }];
      updatedPostal[index || 0] = {
        postalCountry: form.country,
        postalCodes: form.postalCodes.filter(code => code.trim() !== '')
      };
      return { ...prev, postal: updatedPostal };
    });
  };

  const addPostalCode = () => {
    setForm(prev => ({ ...prev, postalCodes: [...prev.postalCodes, ''] }));
    // Persist immediately so parent has latest array length
    setTimeout(() => onBlur(), 0);
  };

  const removePostalCode = (removeIndex: number) => {
    setForm(prev => ({
      ...prev,
      postalCodes: prev.postalCodes.filter((_, idx) => idx !== removeIndex)
    }));
    // Persist after removal
    setTimeout(() => onBlur(), 0);
  };

  return (
    <Box mb={2} border={1} borderColor="border.default" p={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('Postal Codes')}
        </Typography>
        <IconButton aria-label={t('Add Postal Code')} size="small" onClick={addPostalCode}>
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Autocomplete
        options={Object.values(CountryCode).map((code) => ({
          label: COUNTRY_NAMES[code],
          value: code,
          flag: COUNTRY_TO_FLAG[code] || ''
        }))}
        getOptionLabel={(option) => option.label}
        value={form.country ? Object.values(CountryCode).map((code) => ({
          label: COUNTRY_NAMES[code],
          value: code,
          flag: COUNTRY_TO_FLAG[code] || ''
        })).find(opt => opt.value === form.country) || null : null}
        onChange={(e, newValue) => handleChange('country', newValue?.value || '')}
        onBlur={() => onBlur()}
        renderOption={(props, option) => (
          <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{option.flag}</span>
            <span>{option.label}</span>
          </Box>
        )}
        renderInput={(params) => {
          const selectedOption = form.country ? Object.values(CountryCode).map((code) => ({
            label: COUNTRY_NAMES[code],
            value: code,
            flag: COUNTRY_TO_FLAG[code] || ''
          })).find(opt => opt.value === form.country) : null;
          return (
            <TextInput
              {...params}
              name="Country"
              label={t('Country Code')}
              placeholder="e.g. ES"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedOption?.flag ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <span>{selectedOption.flag}</span>
                  </Box>
                ) : null,
              }}
            />
          );
        }}
      />
      <Stack direction="column" spacing={1} mt={1.5}>
        {form.postalCodes.map((code, idx) => (
          <TextInput
            key={idx}
            name={`postalCode-${idx}`}
            label={t('Postal Code')}
            value={code || ''}
            onChange={e => handleChange('postalCodes', e.target.value, idx)}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
            onBlur={onBlur}
            placeholder="e.g. 28001"
            sx={{ flex: 1 }}
            InputProps={{
              endAdornment: form.postalCodes.length > 1 ? (
                <IconButton aria-label={t('Remove Postal Code')} size="small" onClick={() => removePostalCode(idx)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};
